// BUILD-046 section D (Owner direction of 2026-10-21): personal Presets for the My Work toolbar - what a preset holds, how one is named, saved, updated, renamed, deleted and made the
// default of its tab, and when it counts as Modified.
//
// A preset is a SAVED VIEW of the loaded list: the status choice, the quick filter, the department, the Customer / Job, Customer and Assigned to filters, and the sort (a single mode, or up to
// three ordered rules such as Priority then Task due date). It holds no task, no person other than the id the Assigned to filter already names, and no permission: applying it only
// narrows or orders rows the server already returned to this viewer, so it can never show more than the viewer may see (a filter whose person, job or customer is not among the loaded rows
// simply matches nothing). Density and display settings (Comfortable / Compact, Show requester) are NOT part of a preset and are never changed by one. Presets are personal: there is
// no company-wide preset and no shared one. The search text is not saved.
//
// Pure module. Runtime imports: the page sort modes. Erasable TypeScript only.
import { applicablePageSortModes, isPageSortMode } from "./pageSortModel";
import type { PageSortMode } from "./pageSortModel";
import type { AssigneeFilter } from "./assigneeFilterModel";
import type { JobFilter } from "./jobFilterModel";
import type { CustomerFilter } from "./customerFilterModel";
import type { OrderDirection, OrderKey } from "./taskOrdering";
import type { IssuedOrderKey } from "./issuedOrdering";

/** The tabs a preset belongs to; each tab has its own presets and its own default. */
export type PresetTab = "my-work" | "group-shared" | "team-work" | "issued";
export const PRESET_TABS: readonly PresetTab[] = Object.freeze(["my-work", "group-shared", "team-work", "issued"]);

/** What a rule can order by: a column of the task tables, or a column of the Assigned by Me table (its own request-level values). */
export type RuleKey = OrderKey | IssuedOrderKey;

export interface SortRule {
  key: RuleKey;
  direction: OrderDirection;
}

export const MAX_SORT_RULES = 3;
export const MAX_PRESETS = 20;
export const MAX_PRESET_NAME = 40;

/** Everything a preset holds. A field the tab has no control for stays at its empty value. */
export interface PresetCriteria {
  /** My Tasks: the chosen status tab. */
  status: string;
  /** My Tasks: the quick filter (all, urgent, high, due-today, overdue, no-due). */
  quick: string;
  /** Company / Team: the Status choice (open = All open, active, in_progress, waiting, blocked, completed, overdue). */
  open: string;
  department: string;
  job: JobFilter;
  /** The Customer filter (BUILD-046 Customer association). A preset saved before it existed has none: it loads as "any customer" and its job filter is unchanged. */
  customer: CustomerFilter;
  assignee: AssigneeFilter;
  /** The single sort mode; used when there are no rules. */
  mode: PageSortMode;
  /** Ordered sort rules (the first is the main one). Empty means the single mode applies. */
  rules: SortRule[];
}

export interface Preset {
  id: string;
  name: string;
  criteria: PresetCriteria;
  /** ISO instant of the last save. */
  updatedAt: string;
}

export interface PresetBook {
  presets: Preset[];
  /** The preset applied when the tab is first opened, or null. */
  defaultId: string | null;
}

export const EMPTY_BOOK: PresetBook = Object.freeze({ presets: [], defaultId: null }) as PresetBook;

const STATUSES: readonly string[] = ["open", "active", "in_progress", "waiting", "blocked", "completed"];
const QUICK: readonly string[] = ["all", "urgent", "high", "due-today", "overdue", "follow-up-due", "no-due"];
// Company / Team holds the seven Status choices (All open, Active, In progress, Waiting, Blocked, Completed, Overdue) in the field it always called `open`; the two values it saved before (open, completed) are among them.
const OPEN: readonly string[] = ["open", "active", "in_progress", "waiting", "blocked", "completed", "overdue", "follow-up-due"];
// Pass F1 (2026-10-25): Assigned by Me and Shared Tasks save their Status choice in `status`. Assigned by Me also offers "all" (every request); Shared Tasks holds unclaimed open work, so only open and overdue.
const ISSUED_STATUSES: readonly string[] = ["open", "active", "in_progress", "waiting", "blocked", "completed", "overdue", "follow-up-due", "all"];
const SHARED_STATUSES: readonly string[] = ["open", "overdue"];

function statusFor(tab: PresetTab, raw: unknown): string {
  const value = textOf(raw);
  const allowed = tab === "my-work" ? STATUSES : tab === "issued" ? ISSUED_STATUSES : tab === "group-shared" ? SHARED_STATUSES : [];
  return allowed.includes(value) ? value : "";
}
const RULE_KEYS: readonly OrderKey[] = ["title", "assignee", "priority", "due", "status"];

/** The keys of the Assigned by Me table, in the order the panel lists them. issuedOrdering.ts ISSUED_ORDER_KEYS holds the same keys (a test keeps the two equal). */
const ISSUED_RULE_KEYS: readonly IssuedOrderKey[] = ["title", "responsible", "progress", "priority", "due", "assigned"];

/**
 * The rule keys a tab can order by. Assigned to and Status are columns of the Company / Team table only. Assigned by Me orders REQUESTS, so it has its own keys (Task, Assigned to,
 * Status / progress, Priority, Task due date, Assigned / age) and orders by the request-level values its table shows; it never invents one (see ISSUED_RULES_NOTE).
 */
export function ruleKeysFor(tab: PresetTab): RuleKey[] {
  if (tab === "team-work") return [...RULE_KEYS];
  if (tab === "issued") return [...ISSUED_RULE_KEYS];
  return RULE_KEYS.filter((key) => key !== "assignee" && key !== "status");
}

export const RULE_KEY_LABELS: Readonly<Record<RuleKey, string>> = Object.freeze({
  title: "Task",
  assignee: "Assigned to",
  priority: "Priority",
  due: "Task due date",
  status: "Status",
  responsible: "Assigned to",
  progress: "Status",
  assigned: "Assigned / Age",
});

export function ruleDirectionLabel(key: RuleKey, direction: OrderDirection): string {
  if (key === "title" || key === "assignee" || key === "responsible") return direction === "asc" ? "A to Z" : "Z to A";
  if (key === "priority") return direction === "desc" ? "Highest first" : "Lowest first";
  if (key === "due") return direction === "asc" ? "Earliest first" : "Latest first";
  if (key === "assigned") return direction === "desc" ? "Newest first" : "Oldest first";
  if (key === "progress") return direction === "asc" ? "Unclaimed to Cancelled" : "Cancelled to Unclaimed";
  return direction === "asc" ? "Active to Completed" : "Completed to Active";
}

/** Said under the rules of Assigned by Me: a missing value is never guessed. */
export const ISSUED_RULES_NOTE = "A request with no value for a rule is placed last under that rule, and the next rule then orders it. An Everyone request is ordered by the priority or Task due date all its recipients share; where they differ it shows Varies and is placed last under that rule. A restricted task hides them, so nothing is guessed for them.";

/**
 * What a sort covers when the list is read page by page: only the rows loaded so far. Null when every row is loaded (nothing to caveat). Rules, like every other sort of the page,
 * order the loaded rows; rows that load later are placed among them by the same rules.
 */
export function rulesScopeNote(scope: { complete: boolean; loaded: number; singular: string; plural: string }): string | null {
  if (scope.complete) return null;
  const noun = scope.loaded === 1 ? scope.singular : scope.plural;
  return "Sorting covers the " + scope.loaded + " " + noun + " loaded so far, not the ones still to load. When you load more, they are placed among these by the same order.";
}

/** "Priority, highest first, then Task due date, earliest first" - the rules in words, in order. Empty for no rules. */
export function describeRules(rules: readonly SortRule[]): string {
  return rules.map((rule) => RULE_KEY_LABELS[rule.key] + ", " + ruleDirectionLabel(rule.key, rule.direction).toLowerCase()).join(", then ");
}

/** The short text of the Sort button while rules apply (Owner direction 2026-10-23): the first rule and how many more follow it, "Priority +1". The whole order is `describeRules`, shown inside the menu. */
export function rulesButtonText(rules: readonly SortRule[]): string {
  if (rules.length === 0) return "";
  const first = RULE_KEY_LABELS[rules[0].key];
  return rules.length === 1 ? first : first + " +" + (rules.length - 1);
}

/** The empty criteria of a tab: no narrowing, Recommended. */
export function blankCriteria(): PresetCriteria {
  return { status: "", quick: "", open: "", department: "", job: { kind: "any" }, customer: { kind: "any" }, assignee: { kind: "any" }, mode: "recommended", rules: [] };
}

function textOf(value: unknown, max = 200): string {
  return typeof value === "string" ? value.trim().slice(0, max) : "";
}

function jobOf(raw: unknown): JobFilter {
  if (raw === null || typeof raw !== "object") return { kind: "any" };
  const value = raw as { kind?: unknown; jobId?: unknown; label?: unknown };
  if (value.kind === "none") return { kind: "none" };
  if (value.kind === "job" && textOf(value.jobId) !== "") return { kind: "job", jobId: textOf(value.jobId), label: textOf(value.label) };
  return { kind: "any" };
}

function customerOf(raw: unknown): CustomerFilter {
  if (raw === null || typeof raw !== "object") return { kind: "any" };
  const value = raw as { kind?: unknown; customerId?: unknown; label?: unknown };
  if (value.kind === "none") return { kind: "none" };
  if (value.kind === "customer" && textOf(value.customerId) !== "") return { kind: "customer", customerId: textOf(value.customerId), label: textOf(value.label) };
  return { kind: "any" };
}

function assigneeOf(raw: unknown): AssigneeFilter {
  if (raw === null || typeof raw !== "object") return { kind: "any" };
  const value = raw as { kind?: unknown; employeeId?: unknown; label?: unknown; fullName?: unknown; detail?: unknown };
  if (value.kind === "person" && textOf(value.employeeId) !== "") {
    return { kind: "person", employeeId: textOf(value.employeeId), label: textOf(value.label), fullName: textOf(value.fullName), detail: textOf(value.detail) };
  }
  return { kind: "any" };
}

/** The rules a tab can use, from anything: unknown keys, repeated keys, a bad direction and more than three rules are dropped. */
export function normalizeRules(tab: PresetTab, raw: unknown): SortRule[] {
  if (!Array.isArray(raw)) return [];
  const allowed = ruleKeysFor(tab);
  const out: SortRule[] = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== "object") continue;
    const { key, direction } = entry as { key?: unknown; direction?: unknown };
    if (typeof key !== "string" || !allowed.includes(key as RuleKey) || out.some((rule) => rule.key === key)) continue;
    if (direction !== "asc" && direction !== "desc") continue;
    out.push({ key: key as RuleKey, direction });
    if (out.length === MAX_SORT_RULES) break;
  }
  return out;
}

/** Criteria from anything (a stored value may be old, edited or from another version): only what this tab can hold survives, everything else is the empty value. */
export function normalizeCriteria(tab: PresetTab, raw: unknown): PresetCriteria {
  const value = raw !== null && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const base = blankCriteria();
  const rules = normalizeRules(tab, value.rules);
  const view = tab === "issued" ? "issued" : tab;
  const modeRaw = value.mode;
  const mode: PageSortMode = isPageSortMode(modeRaw) && applicablePageSortModes(view).includes(modeRaw) ? modeRaw : "recommended";
  return {
    ...base,
    status: statusFor(tab, value.status),
    quick: tab === "my-work" && QUICK.includes(textOf(value.quick)) ? textOf(value.quick) : "",
    open: tab === "team-work" && OPEN.includes(textOf(value.open)) ? textOf(value.open) : "",
    department: tab === "team-work" ? textOf(value.department, 80) : "",
    job: tab === "issued" ? { kind: "any" } : jobOf(value.job),
    customer: tab === "issued" ? { kind: "any" } : customerOf(value.customer),
    assignee: tab === "team-work" || tab === "issued" ? assigneeOf(value.assignee) : { kind: "any" },
    mode: rules.length > 0 ? "recommended" : mode,
    rules,
  };
}

/** A stable text of the criteria for comparing two of them. The display labels of a filter are not part of it (a job or a customer renamed in the directory, or a name spelled differently, is the same job, customer and person). */
export function criteriaKey(tab: PresetTab, criteria: PresetCriteria): string {
  const c = normalizeCriteria(tab, criteria);
  return JSON.stringify({
    // A My Tasks preset saved with a blank status always meant the page default of its day, Active. The page default is All open now, but the saved preset keeps its meaning, so a blank
    // status and an explicit "active" are the same preset (2026-10-24 Owner correction 1: existing presets are not rewritten).
    // Assigned by Me and Shared Tasks never had a saved status before pass F1; a blank one there means the page default, All open. Company / Team: a blank choice is All open as well.
    status: tab === "my-work" && c.status === "" ? "active" : (tab === "issued" || tab === "group-shared") && c.status === "" ? "open" : c.status,
    // A My Tasks preset written with a blank quick filter means "all" (the page's own value), so the two are the same preset.
    quick: tab === "my-work" && c.quick === "" ? "all" : c.quick,
    open: tab === "team-work" && c.open === "" ? "open" : c.open,
    department: c.department,
    job: c.job.kind === "job" ? { kind: "job", jobId: c.job.jobId } : { kind: c.job.kind },
    customer: c.customer.kind === "customer" ? { kind: "customer", customerId: c.customer.customerId } : { kind: c.customer.kind },
    assignee: c.assignee.kind === "person" ? { kind: "person", employeeId: c.assignee.employeeId } : { kind: "any" },
    mode: c.mode,
    rules: c.rules,
  });
}

export function sameCriteria(tab: PresetTab, a: PresetCriteria, b: PresetCriteria): boolean {
  return criteriaKey(tab, a) === criteriaKey(tab, b);
}

// ───────────────────────── names ─────────────────────────

export type NameCheck = { ok: true; name: string } | { ok: false; message: string };

/** A preset name: trimmed, 1 to 40 characters, and not another preset of the same tab (compared without regard to case). `exceptId` is the preset being renamed. */
export function checkName(book: PresetBook, raw: unknown, exceptId: string | null = null): NameCheck {
  const name = typeof raw === "string" ? raw.replace(/\s+/g, " ").trim() : "";
  if (name === "") return { ok: false, message: "Enter a name for the preset." };
  if (name.length > MAX_PRESET_NAME) return { ok: false, message: "A preset name can have up to " + MAX_PRESET_NAME + " characters." };
  if (book.presets.some((p) => p.id !== exceptId && p.name.toLowerCase() === name.toLowerCase())) return { ok: false, message: "You already have a preset with that name on this tab." };
  return { ok: true, name };
}

// ───────────────────────── the book of one tab ─────────────────────────

export type BookResult = { ok: true; book: PresetBook; preset: Preset } | { ok: false; message: string };

export function addPreset(book: PresetBook, tab: PresetTab, rawName: unknown, criteria: PresetCriteria, id: string, now: string): BookResult {
  if (book.presets.length >= MAX_PRESETS) return { ok: false, message: "You can keep up to " + MAX_PRESETS + " presets on a tab. Delete one first." };
  const named = checkName(book, rawName);
  if (!named.ok) return named;
  const preset: Preset = { id, name: named.name, criteria: normalizeCriteria(tab, criteria), updatedAt: now };
  return { ok: true, book: { presets: [...book.presets, preset], defaultId: book.defaultId }, preset };
}

export function renamePreset(book: PresetBook, id: string, rawName: unknown, now: string): BookResult {
  const found = book.presets.find((p) => p.id === id);
  if (!found) return { ok: false, message: "That preset is no longer there." };
  const named = checkName(book, rawName, id);
  if (!named.ok) return named;
  const preset: Preset = { ...found, name: named.name, updatedAt: now };
  return { ok: true, book: { presets: book.presets.map((p) => (p.id === id ? preset : p)), defaultId: book.defaultId }, preset };
}

/** Replaces what a preset holds with the current view. Only this is "Update": a manual change never overwrites a preset by itself. */
export function updatePreset(book: PresetBook, tab: PresetTab, id: string, criteria: PresetCriteria, now: string): BookResult {
  const found = book.presets.find((p) => p.id === id);
  if (!found) return { ok: false, message: "That preset is no longer there." };
  const preset: Preset = { ...found, criteria: normalizeCriteria(tab, criteria), updatedAt: now };
  return { ok: true, book: { presets: book.presets.map((p) => (p.id === id ? preset : p)), defaultId: book.defaultId }, preset };
}

/**
 * Owner direction 2026-10-23 (Edit preset): changes a preset's NAME and what it holds in one step, and keeps its id, its place in the list and whether it is the default. The name may be the
 * preset's own (the check excludes it). Nothing is created, so no second preset with the old name remains.
 */
export function editPreset(book: PresetBook, tab: PresetTab, id: string, rawName: unknown, criteria: PresetCriteria, now: string): BookResult {
  const found = book.presets.find((p) => p.id === id);
  if (!found) return { ok: false, message: "That preset is no longer there." };
  const named = checkName(book, rawName, id);
  if (!named.ok) return named;
  const preset: Preset = { ...found, name: named.name, criteria: normalizeCriteria(tab, criteria), updatedAt: now };
  return { ok: true, book: { presets: book.presets.map((p) => (p.id === id ? preset : p)), defaultId: book.defaultId }, preset };
}

export type MoveWhere = "top" | "up" | "down";

/**
 * Owner direction 2026-10-23: the order of the list of presets is the person's own and is separate from how the tasks are sorted. The array order of the stored book IS that order (no new field,
 * so books saved before this still read as they were). A move changes only the position: never the default, never what a preset holds. A move that cannot happen (already first, already last,
 * unknown id) returns the book unchanged.
 */
export function movePreset(book: PresetBook, id: string, where: MoveWhere): PresetBook {
  const from = book.presets.findIndex((p) => p.id === id);
  if (from < 0) return book;
  const to = where === "top" ? 0 : where === "up" ? from - 1 : from + 1;
  if (to < 0 || to >= book.presets.length || to === from) return book;
  const next = book.presets.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return { presets: next, defaultId: book.defaultId };
}

/** Deletes a preset; if it was the default of the tab, the tab has no default after. */
export function deletePreset(book: PresetBook, id: string): PresetBook {
  return { presets: book.presets.filter((p) => p.id !== id), defaultId: book.defaultId === id ? null : book.defaultId };
}

/** Makes a preset the default of its tab (null: no default). A preset that is not in the book cannot be the default. */
export function setDefault(book: PresetBook, id: string | null): PresetBook {
  if (id === null) return { presets: book.presets, defaultId: null };
  return book.presets.some((p) => p.id === id) ? { presets: book.presets, defaultId: id } : book;
}

export function presetOf(book: PresetBook, id: string | null): Preset | null {
  return id === null ? null : book.presets.find((p) => p.id === id) ?? null;
}

/** A book from anything stored: a bad entry is dropped, ids and names are kept unique, the default must exist, at most MAX_PRESETS survive. */
export function parseBook(tab: PresetTab, raw: unknown): PresetBook {
  if (raw === null || typeof raw !== "object") return EMPTY_BOOK;
  const value = raw as { presets?: unknown; defaultId?: unknown };
  const presets: Preset[] = [];
  if (Array.isArray(value.presets)) {
    for (const entry of value.presets) {
      if (entry === null || typeof entry !== "object") continue;
      const e = entry as { id?: unknown; name?: unknown; criteria?: unknown; updatedAt?: unknown };
      const id = textOf(e.id, 80);
      const name = typeof e.name === "string" ? e.name.replace(/\s+/g, " ").trim().slice(0, MAX_PRESET_NAME) : "";
      if (id === "" || name === "" || presets.some((p) => p.id === id || p.name.toLowerCase() === name.toLowerCase())) continue;
      presets.push({ id, name, criteria: normalizeCriteria(tab, e.criteria), updatedAt: textOf(e.updatedAt, 40) });
      if (presets.length === MAX_PRESETS) break;
    }
  }
  const defaultId = typeof value.defaultId === "string" && presets.some((p) => p.id === value.defaultId) ? value.defaultId : null;
  return { presets, defaultId };
}

/** The tabs a Caller has: a tab key from the page maps to a preset tab (the host view, or "issued"). */
export function presetTabOf(tab: string): PresetTab | null {
  return (PRESET_TABS as readonly string[]).includes(tab) ? (tab as PresetTab) : null;
}
